import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, AreaField, DateField } from "@/components/fields";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { IdentificationCard, Plus, PencilSimple, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = {
  full_name: "", national_id: "", kra_pin: "", phone: "", email: "", dob: "",
  employer: "", occupation: "", work_address: "", supervisor_contact: "", monthly_income: 0,
  emergency_name: "", emergency_relationship: "", emergency_phone: "", letting_staff_id: "",
  status: "prospective", documents: {},
};

const STEPS = [
  ["kyc", "KYC Verification"], ["lease", "Lease Preparation"], ["execution", "Lease Execution"],
  ["deposit", "Security Deposit"], ["first_rent", "First Rent Payment"], ["allocation", "Unit Allocation"],
  ["inspection", "Site Inspection"], ["inventory", "Inventory Capture"], ["handover", "Key Handover"],
];

export default function Tenants() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [ob, setOb] = useState(null);

  const canEdit = ["property_manager", "property_admin", "staff"].includes(user.role);

  const load = () => {
    api.get("/tenants").then((r) => setItems(r.data));
    api.get("/users").then((r) => setStaff(r.data.filter((u) => u.role === "staff"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...empty, ...t }); setEditId(t.id); setOpen(true); };

  const save = async () => {
    if (!form.full_name) return toast.error("Full name is required");
    try {
      const payload = { ...form, letting_staff_id: form.letting_staff_id || null };
      if (editId) await api.put(`/tenants/${editId}`, payload);
      else await api.post("/tenants", payload);
      toast.success(editId ? "Tenant updated" : "Tenant registered");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleStep = async (tenant, step, done) => {
    const r = await api.post(`/tenants/${tenant.id}/onboarding`, { step, done });
    setOb({ ...ob, onboarding: r.data.onboarding }); load();
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Tenants" subtitle="Prospective & active tenants (KYC)" icon={IdentificationCard}
        actions={canEdit && <Button onClick={openNew} data-testid="add-tenant-btn" className="gap-2"><Plus size={16} /> Register Tenant</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={IdentificationCard} title="No tenants yet" message="Register a prospective tenant to begin letting."
          action={canEdit && <Button onClick={openNew}><Plus size={16} className="mr-2" />Register Tenant</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Occupation</TableHead>
                <TableHead className="text-right">Income</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((t) => (
                  <TableRow key={t.id} data-testid={`tenant-row-${t.id}`}>
                    <TableCell><div className="font-medium">{t.full_name}</div><div className="text-xs text-muted-foreground">{t.national_id}</div></TableCell>
                    <TableCell className="text-sm">{t.phone}<div className="text-xs text-muted-foreground">{t.email}</div></TableCell>
                    <TableCell className="text-sm">{t.occupation || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{money(t.monthly_income)}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => setOb(t)} data-testid={`onboard-${t.id}`}><ListChecks size={16} /></Button>
                        {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(t)}><PencilSimple size={16} /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Create/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Tenant" : "Register Tenant (KYC)"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="text-sm font-semibold text-primary">Personal Details</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Full Name" value={form.full_name} onChange={(v) => set("full_name", v)} required testId="tenant-name" />
              <TextField label="National ID / Passport" value={form.national_id} onChange={(v) => set("national_id", v)} />
              <TextField label="KRA PIN" value={form.kra_pin} onChange={(v) => set("kra_pin", v)} />
              <TextField label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testId="tenant-phone" />
              <TextField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="tenant-email" />
              <DateField label="Date of Birth" value={form.dob} onChange={(v) => set("dob", v)} />
            </div>
            <div className="text-sm font-semibold text-primary">Employment</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Employer / Company" value={form.employer} onChange={(v) => set("employer", v)} />
              <TextField label="Occupation" value={form.occupation} onChange={(v) => set("occupation", v)} />
              <TextField label="Work Address" value={form.work_address} onChange={(v) => set("work_address", v)} />
              <TextField label="Supervisor Contact" value={form.supervisor_contact} onChange={(v) => set("supervisor_contact", v)} />
              <TextField label="Monthly Income" type="number" value={form.monthly_income} onChange={(v) => set("monthly_income", v)} />
            </div>
            <div className="text-sm font-semibold text-primary">Emergency Contact</div>
            <div className="grid sm:grid-cols-3 gap-4">
              <TextField label="Name" value={form.emergency_name} onChange={(v) => set("emergency_name", v)} />
              <TextField label="Relationship" value={form.emergency_relationship} onChange={(v) => set("emergency_relationship", v)} />
              <TextField label="Phone" value={form.emergency_phone} onChange={(v) => set("emergency_phone", v)} />
            </div>
            <SelectField label="Letting / Sourcing Staff" value={form.letting_staff_id} onChange={(v) => set("letting_staff_id", v)}
              options={[{ value: "", label: "— None —" }, ...staff.map((s) => ({ value: s.id, label: s.name }))]} testId="tenant-letting" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-tenant-btn">{editId ? "Save" : "Register"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding */}
      <Dialog open={!!ob} onOpenChange={() => setOb(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Onboarding — {ob?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {STEPS.map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 p-2.5 rounded-md border border-border cursor-pointer hover:bg-secondary">
                <Checkbox checked={!!ob?.onboarding?.[key]} onCheckedChange={(v) => toggleStep(ob, key, !!v)} data-testid={`step-${key}`} />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
